import { useContext, useState } from 'react';
import styled from 'styled-components';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { media } from 'src/styles/designs';
import { useTranslate } from 'react-t7e';
import { AnimatePresence } from 'framer-motion';
import { ScrollToViewport } from 'src/components/common/ScrollToViewport';
import { Theme } from 'vendor/mods/styled-components';
import { redTheme } from 'src/styles/theme';
import { EventPageFAQItem } from 'src/modules/events/components/EventPage/EventPageFAQSection/EventPageFAQItem';
import { ExpandTransition } from 'src/components/common/ExpandTransition';
import { EventPageHeading } from 'src/modules/events/components/EventPage/EventPageHeading';
import { EventPageContext } from 'src/modules/events/components/EventPage/EventPageContext';
import { EventPageHowToParticipate } from 'src/modules/events/components/EventPage/EventPageHowToParticipate';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';

const Background = styled.div`
  position: relative;
  margin-top: 40px;
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: calc(100% + var(--footer-static-height));
    background: linear-gradient(263.5deg, #a2002a 35.42%, #cd0338 92.44%),
      linear-gradient(259.64deg, #a2002a -68.45%, #cd0338 95.71%);
  }
  ${media.w.greaterThanOrEqual.desktop} {
    margin-top: 70px;
  }
`;

const Section = styled.section`
  padding: 30px 0 34px;
  ${media.w.greaterThanOrEqual.desktop} {
    padding: 54px 0 76px;
  }
`;

const Container = styled(ConstraintContainer)`
  position: relative;
  max-width: 1020px;
  z-index: 1;
`;

const ContentContainer = styled(ConstraintContainer)`
  max-width: 660px;
  padding: 0 !important;
  margin-top: 40px;
`;

const HowToParticipateSection = styled(EventPageHowToParticipate)`
  position: relative;
  z-index: 1;
  background: transparent;
  ${media.w.greaterThanOrEqual.desktop} {
    & > div {
      position: relative;
      max-width: 940px;
      &:before {
        content: '';
        background-color: currentColor;
        position: absolute;
        top: 4px;
        bottom: 0;
        height: 83px;
        left: 0;
        width: 19px;
        clip-path: polygon(0 0, 40% 0, 100% 100%, 90% 100%);
      }
    }
  }
`;

export const EventPageFAQSection = () => {
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const { event } = useContext(EventPageContext);

  const { _ } = useTranslate();
  const translateItem = useTranslateItem();

  if (event.faq?.length === 0) return null;

  return (
    <Theme theme={redTheme}>
      <Background>
        <Section>
          <Container>
            <EventPageHeading>{_('faq')}</EventPageHeading>
            <ContentContainer>
              <AnimatePresence initial={false}>
                {event.faq
                  .map((item) => translateItem(item.eventFaq_id))
                  .filter(({ question, answer }) => question && answer)
                  .map(({ question, answer }) => {
                    const isExpanded = question === expandedItem;
                    return (
                      <ScrollToViewport key={question} active={isExpanded}>
                        <ExpandTransition>
                          <EventPageFAQItem
                            label={question}
                            answer={answer}
                            isExpanded={isExpanded}
                            toggleIsExpanded={() =>
                              isExpanded
                                ? setExpandedItem(null)
                                : setExpandedItem(question)
                            }
                          />
                        </ExpandTransition>
                      </ScrollToViewport>
                    );
                  })}
              </AnimatePresence>
            </ContentContainer>
          </Container>
        </Section>
        <HowToParticipateSection />
      </Background>
    </Theme>
  );
};
